import { useState, useEffect, useMemo } from 'react'
import type { Patient, Practitioner, Coverage, Condition, Organization } from '@medplum/fhirtypes'
import { WithId } from '@medplum/core'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { useMiddlewareClient } from '@/hooks'
import { formatProviderName } from '@/utils/providerUtils'
import { getInsuranceDisplayData } from '@/utils/coverageUtils'
import {
  getPatientName,
  formatFhirDob,
  getPatientAddress,
  getPatientPhone,
  getPatientEmail,
  getPatientSex,
} from './formHelpers'
import type {
  PatientDefaults,
  ProviderDefaults,
  InsuranceDefaults,
  DiagnosisDefault,
} from './types'
import { logError } from './errorUtils'
import { SEEDED_LAB_ACCOUNT_NUMBERS } from '@/model/middleware-types'

interface OrgDefaults {
  organizationName: string
  orgPhone: string
  orgFax: string
  orgAddress: string
  orgCity: string
  orgState: string
  orgZip: string
  orgCountry: string
  labAccountNumbers: Record<string, string>
}

interface ResolvedCoveragesResult {
  patientId: string
  mode: 'hg' | 'local-only'
  primary?: Coverage
  secondary?: Coverage
  tertiary?: Coverage
}

const EMPTY_ORG: OrgDefaults = {
  organizationName: '',
  orgPhone: '',
  orgFax: '',
  orgAddress: '',
  orgCity: '',
  orgState: '',
  orgZip: '',
  orgCountry: '',
  labAccountNumbers: {},
}

function extractOrgDefaults(org: Organization): OrgDefaults {
  const fax = org.telecom?.find((t) => t.system === 'fax')?.value || ''
  const phone = org.telecom?.find((t) => t.system === 'phone')?.value || ''
  const addr = org.address?.[0]

  const labAccountNumbers: Record<string, string> = {}
  for (const id of org.identifier || []) {
    // A seeded placeholder is not an account number the lab would recognise, so it counts
    // as unset here rather than being printed on the requisition as though it were real.
    if (
      id.system?.startsWith('https://behuman.health/lab-account/') &&
      id.value &&
      !SEEDED_LAB_ACCOUNT_NUMBERS.includes(id.value)
    ) {
      labAccountNumbers[id.system] = id.value
    }
  }

  return {
    organizationName: org.name || '',
    orgPhone: phone,
    orgFax: fax,
    orgAddress: addr?.line?.join(', ') || '',
    orgCity: addr?.city || '',
    orgState: addr?.state || '',
    orgZip: addr?.postalCode || '',
    orgCountry: addr?.country || '',
    labAccountNumbers,
  }
}

/**
 * Hook that returns auto-populated defaults for patient, provider, and insurance.
 * These values can be used to initialize any vendor form state.
 */
export function useAutoPopulate(patient: Patient) {
  const medplum = useMedplum()
  const profile = useMedplumProfile() as WithId<Practitioner> | undefined
  const middleware = useMiddlewareClient()
  const middlewareClient = middleware as unknown as {
    get: <T = unknown>(url: string) => Promise<{ result: T }>
  }

  const patientDefaults = useMemo<PatientDefaults>(() => {
    const name = getPatientName(patient)
    const addr = getPatientAddress(patient)
    return {
      firstName: name.firstName,
      middleName: name.middleName,
      lastName: name.lastName,
      dateOfBirth: formatFhirDob(patient.birthDate),
      sexCode: getPatientSex(patient),
      email: getPatientEmail(patient),
      phone: getPatientPhone(patient),
      address: addr.line,
      apt: '',
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
      country: addr.country,
      patientId: patient.id || '',
      cityStateZip: addr.cityStateZip,
    }
  }, [patient])

  const practitionerFields = useMemo(() => {
    if (!profile)
      return {
        providerName: '',
        providerFirstName: '',
        providerLastName: '',
        providerEmail: '',
        npi: '',
      }
    const names = profile.name?.[0]
    return {
      providerName: formatProviderName(names),
      providerFirstName: names?.given?.[0] || '',
      providerLastName: names?.family || '',
      providerEmail: profile.telecom?.find((t) => t.system === 'email')?.value || '',
      npi:
        profile.identifier?.find((id) => id.system === 'http://hl7.org/fhir/sid/us-npi')?.value ||
        '',
    }
  }, [profile])

  const [orgDefaults, setOrgDefaults] = useState<OrgDefaults>(EMPTY_ORG)
  const [orgError, setOrgError] = useState<string | null>(null)

  useEffect(() => {
    if (!profile?.id) {
      setOrgError(
        'No practitioner profile found. Please log in again or contact your administrator.',
      )
      return
    }
    let mounted = true

    const loadOrganization = async () => {
      try {
        const role = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        if (!role?.organization?.reference) {
          if (mounted) {
            setOrgError(
              'Your account does not have an organization assigned. Ask your administrator to link a PractitionerRole with an Organization to your profile.',
            )
          }
          return
        }
        const org = await medplum.readReference(role.organization)
        if (mounted) {
          setOrgDefaults(extractOrgDefaults(org as Organization))
          setOrgError(null)
        }
      } catch (err) {
        logError('Organization lookup', err)
        if (mounted) {
          setOrgError(
            'Failed to load your organization details. Please refresh the page or contact support.',
          )
        }
      }
    }

    loadOrganization()
    return () => {
      mounted = false
    }
  }, [medplum, profile?.id])

  const providerDefaults = useMemo<ProviderDefaults>(
    () => ({
      ...practitionerFields,
      ...orgDefaults,
    }),
    [practitionerFields, orgDefaults],
  )

  const [insuranceDefaults, setInsuranceDefaults] = useState<InsuranceDefaults>({
    primaryCarrier: '',
    primaryMemberId: '',
    primaryGroup: '',
    plan: '',
    policyholderName: '',
    relationship: '',
  })
  const [insuranceWarning, setInsuranceWarning] = useState<string | null>(null)
  /** False until the coverage lookup settles, so a caller can fail closed
   *  instead of reading "no warning yet" as "insured". */
  const [insuranceResolved, setInsuranceResolved] = useState(false)

  useEffect(() => {
    // Back to unresolved for every run. `PatientPage` renders chart sections
    // with no key, so switching patient re-renders an open order form instead
    // of remounting it — and a stale `true` here would let the new patient's
    // order clear the insurance gate on the previous patient's answer.
    setInsuranceResolved(false)
    // The request is not addressed to a patient once it is in flight, so a slow
    // lookup for the previous patient could land after this one and write its
    // carrier onto the current patient's requisition. Same guard the
    // organization effect above uses.
    let current = true

    const loadInsurance = async () => {
      try {
        const { result } = await middlewareClient.get<ResolvedCoveragesResult>(
          `/insurance/patient/${patient.id}/coverages`,
        )
        if (!current) return
        const primary = result.primary

        const defaults: InsuranceDefaults = {
          primaryCarrier: '',
          primaryMemberId: '',
          primaryGroup: '',
          plan: '',
          policyholderName: '',
          relationship: '',
        }

        if (primary) {
          const data = getInsuranceDisplayData(primary)
          const relationCode = primary.relationship?.coding?.[0]?.code?.toLowerCase() || ''
          defaults.primaryCarrier = data.payorName
          defaults.primaryMemberId = data.memberId
          defaults.primaryGroup = data.group
          defaults.plan = data.plan
          defaults.policyholderName =
            relationCode === 'self'
              ? [patientDefaults.firstName, patientDefaults.lastName].filter(Boolean).join(' ')
              : primary.subscriber?.display || ''
          defaults.relationship = relationCode || ''
          setInsuranceWarning(null)
        } else {
          setInsuranceWarning(
            "No active insurance found for this patient. Please add insurance details manually or verify the patient's coverage.",
          )
        }

        setInsuranceDefaults(defaults)
      } catch (err) {
        logError('Insurance lookup', err)
        if (!current) return
        setInsuranceWarning(
          'Failed to load insurance information. Please verify insurance details are correct before submitting.',
        )
      } finally {
        // A superseded run must leave the gate closed; the run that replaced it
        // opens it when its own answer arrives.
        if (current) setInsuranceResolved(true)
      }
    }
    loadInsurance()
    return () => {
      current = false
    }
  }, [middlewareClient, patient.id, patientDefaults.firstName, patientDefaults.lastName])

  const [diagnosisDefaults, setDiagnosisDefaults] = useState<DiagnosisDefault[]>([])

  useEffect(() => {
    const loadDiagnoses = async () => {
      try {
        const res = await medplum.search(
          'Condition',
          `patient=Patient/${patient.id}&clinical-status=active&_count=100`,
        )
        const conditions =
          res.entry?.map((e) => e.resource).filter((r): r is WithId<Condition> => !!r) || []

        const codes: DiagnosisDefault[] = []
        for (const condition of conditions) {
          const codings = condition?.code?.coding || []
          for (const coding of codings) {
            if (
              coding.code &&
              (coding.system === 'http://hl7.org/fhir/sid/icd-10-cm' ||
                coding.system === 'http://hl7.org/fhir/sid/icd-10')
            ) {
              codes.push({
                code: coding.code,
                display: coding.display || coding.code,
              })
            }
          }
        }

        // Deduplicate by code
        const unique = Array.from(new Map(codes.map((c) => [c.code, c])).values())
        setDiagnosisDefaults(unique)
      } catch (err) {
        logError('Diagnosis lookup', err)
      }
    }
    loadDiagnoses()
  }, [medplum, patient.id])

  return {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
    insuranceResolved,
  }
}
